/**
 * localStorage Helper - LocalStorage-First Architecture
 *
 * Rules:
 * - localStorage is the source of truth
 * - Only fetch API if data doesn't exist in localStorage
 * - Include version, lastUpdated timestamp with all data
 * - Graceful error handling for corrupted data
 * - Clear cache on logout
 */

const STORAGE_VERSION = "v1";
const USER_PROFILE_KEY = "user_profile";
const USER_MATCHES_KEY = "user_matches";
const MATCH_DETAIL_KEY = (matchId) => `match_${matchId}`;

/**
 * Get data from localStorage
 * @param {string} key - Storage key
 * @returns {object|null} - Data object or null if missing/invalid
 */
export const getLocal = (key) => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const parsed = JSON.parse(item);

    // Validate structure
    if (!parsed.data || !parsed.lastUpdated || !parsed.version) {
      console.warn(`[Storage] Invalid structure for ${key}, clearing`);
      removeLocal(key);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error(`[Storage] Error reading ${key}:`, error);
    removeLocal(key); // Clear corrupted data
    return null;
  }
};

/**
 * Save data to localStorage with metadata
 * @param {string} key - Storage key
 * @param {object} data - Data to store
 */
export const setLocal = (key, data) => {
  try {
    const storageObject = {
      data: data,
      lastUpdated: Date.now(),
      version: STORAGE_VERSION,
    };
    localStorage.setItem(key, JSON.stringify(storageObject));
  } catch (error) {
    console.error(`[Storage] Error writing ${key}:`, error);
    // Fail silently - app continues with in-memory data
  }
};

/**
 * Remove specific key from localStorage
 * @param {string} key - Storage key
 */
export const removeLocal = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`[Storage] Error removing ${key}:`, error);
  }
};

/**
 * Clear all user-related cache (called on logout)
 */
export const clearUserCache = () => {
  try {
    // Get all keys that start with user_ or match_
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith("user_") || key.startsWith("match_")) {
        localStorage.removeItem(key);
      }
    });
    console.log("[Storage] User cache cleared");
  } catch (error) {
    console.error("[Storage] Error clearing user cache:", error);
  }
};

/**
 * Clear profile-specific cache
 */
export const clearProfileCache = () => {
  removeLocal(USER_PROFILE_KEY);
};

/**
 * Clear matches list cache
 */
export const clearMatchesCache = () => {
  removeLocal(USER_MATCHES_KEY);
};

/**
 * Clear specific match detail cache
 * @param {string} matchId - Match ID to clear
 */
export const clearMatchDetailCache = (matchId) => {
  removeLocal(MATCH_DETAIL_KEY(matchId));
};

/**
 * Clear all match detail caches (when matches list changes)
 */
export const clearAllMatchDetailCaches = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith("match_")) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error("[Storage] Error clearing match details:", error);
  }
};

// ==========================================
// PROFILE STORAGE
// ==========================================

/**
 * Get user profile from localStorage
 * @returns {object|null} - { data: userProfile, lastUpdated, version } or null
 */
export const getProfileLocal = () => {
  return getLocal(USER_PROFILE_KEY);
};

/**
 * Save user profile to localStorage
 * @param {object} profileData - User profile object
 */
export const setProfileLocal = (profileData) => {
  setLocal(USER_PROFILE_KEY, profileData);
};

// ==========================================
// MATCHES STORAGE
// ==========================================

/**
 * Get matches list from localStorage
 * @returns {object|null} - { data: matchesArray, lastUpdated, version } or null
 */
export const getMatchesLocal = () => {
  return getLocal(USER_MATCHES_KEY);
};

/**
 * Save matches list to localStorage
 * @param {array} matchesData - Array of match objects
 */
export const setMatchesLocal = (matchesData) => {
  setLocal(USER_MATCHES_KEY, matchesData);
};

// ==========================================
// MATCH DETAIL STORAGE
// ==========================================

/**
 * Get specific match details from localStorage
 * @param {string} matchId - Match ID
 * @returns {object|null} - { data: matchDetail, lastUpdated, version } or null
 */
export const getMatchDetailLocal = (matchId) => {
  return getLocal(MATCH_DETAIL_KEY(matchId));
};

/**
 * Save match details to localStorage
 * @param {string} matchId - Match ID
 * @param {object} matchDetailData - Match detail object
 */
export const setMatchDetailLocal = (matchId, matchDetailData) => {
  setLocal(MATCH_DETAIL_KEY(matchId), matchDetailData);
};

// ==========================================
// CACHE VALIDATION
// ==========================================

/**
 * Check if cached data exists (without expiration - trust on invalidation)
 * @param {string} key - Storage key
 * @returns {boolean}
 */
export const hasLocal = (key) => {
  const data = getLocal(key);
  return data !== null;
};

/**
 * Get cache age in milliseconds
 * @param {string} key - Storage key
 * @returns {number|null} - Age in ms, or null if not cached
 */
export const getCacheAge = (key) => {
  const data = getLocal(key);
  if (!data) return null;
  return Date.now() - data.lastUpdated;
};

/**
 * Check if data is older than specified duration
 * @param {string} key - Storage key
 * @param {number} maxAge - Max age in milliseconds
 * @returns {boolean} - True if expired
 */
export const isCacheExpired = (key, maxAge = Infinity) => {
  const age = getCacheAge(key);
  return age === null || age > maxAge;
};

// ==========================================
// DEBUG HELPERS
// ==========================================

/**
 * Get all cached keys
 * @returns {object} - Map of all cached user data
 */
export const getCacheStats = () => {
  const stats = {};
  const keys = Object.keys(localStorage);

  keys.forEach((key) => {
    if (key.startsWith("user_") || key.startsWith("match_")) {
      const data = getLocal(key);
      stats[key] = {
        age: getCacheAge(key),
        size: JSON.stringify(data).length,
        cached: true,
      };
    }
  });

  return stats;
};

/**
 * Clear all localStorage (warning: use carefully!)
 */
export const clearAllStorage = () => {
  try {
    localStorage.clear();
    console.log("[Storage] All localStorage cleared");
  } catch (error) {
    console.error("[Storage] Error clearing all storage:", error);
  }
};

const storageHelperExport = {
  // Basic operations
  getLocal,
  setLocal,
  removeLocal,

  // Profile
  getProfileLocal,
  setProfileLocal,
  clearProfileCache,

  // Matches
  getMatchesLocal,
  setMatchesLocal,
  clearMatchesCache,

  // Match details
  getMatchDetailLocal,
  setMatchDetailLocal,
  clearMatchDetailCache,
  clearAllMatchDetailCaches,

  // Cache management
  clearUserCache,
  hasLocal,
  getCacheAge,
  isCacheExpired,

  // Debug
  getCacheStats,
  clearAllStorage,
};

export default storageHelperExport;
